import chalk from 'chalk';
import { resolve } from 'path';
import { exploreCodebase, parseExtensions, parseExcludeDirs } from '../lib/explorer.js';

/**
 * Post-onboarding flow presented after successful first login.
 * Offers 3 paths: Guided Integration, Free-flow Optimization, or Exit.
 *
 * IMPORTANT: This function is APPENDED to the auth login success handler.
 * It must NOT modify any auth state or credentials.
 */
export async function runPostOnboarding(): Promise<void> {
  const inquirer = (await import('inquirer')).default;

  console.log('');
  console.log(chalk.bold.cyan('  What would you like to do next?'));
  console.log('');

  const { path: selectedPath } = await inquirer.prompt<{ path: 'integrate' | 'optimize' | 'exit' }>([{
    type: 'list',
    name: 'path',
    message: 'Choose your path:',
    choices: [
      {
        name: `${chalk.green('A')} Guided Integration — connect your AI framework`,
        value: 'integrate',
      },
      {
        name: `${chalk.green('B')} Quick Optimization — upload a prompt and optimize it`,
        value: 'optimize',
      },
      {
        name: `${chalk.green('C')} Exit — explore on your own`,
        value: 'exit',
      },
    ],
  }]);

  if (selectedPath === 'exit') {
    console.log('');
    console.log(chalk.dim('  You can run `mutagent --help` anytime to see available commands.'));
    console.log(chalk.dim('  Try `mutagent explore` to scan your codebase for prompts.'));
    console.log('');
    return;
  }

  // Run explore on current directory
  console.log('');
  console.log(chalk.cyan('  Scanning your codebase...'));
  console.log('');

  const scanPath = resolve('.');
  const result = exploreCodebase({
    path: scanPath,
    depth: 10,
    extensions: parseExtensions('**/*.{ts,js,py,tsx,jsx}'),
    excludeDirs: parseExcludeDirs('node_modules,dist,.git,build,.next,__pycache__,venv,.venv'),
    markersOnly: false,
  });

  const totalFindings = result.prompts.length + result.datasets.length;

  if (totalFindings > 0) {
    console.log(chalk.green(`  Found ${String(result.prompts.length)} prompt(s) and ${String(result.datasets.length)} dataset(s)`));
  } else {
    console.log(chalk.dim('  No prompts or datasets detected yet.'));
  }
  console.log('');

  if (selectedPath === 'integrate') {
    await runIntegrationPath();
  } else {
    runOptimizationPath(result.prompts.length, result.datasets.length);
  }
}

async function runIntegrationPath(): Promise<void> {
  const inquirerMod = (await import('inquirer')).default;
  const { framework } = await inquirerMod.prompt<{ framework: string }>([{
    type: 'list',
    name: 'framework',
    message: 'Which AI framework are you using?',
    choices: [
      { name: 'LangChain', value: 'langchain' },
      { name: 'LangGraph', value: 'langgraph' },
      { name: 'Vercel AI SDK', value: 'vercel-ai' },
      { name: 'OpenAI SDK', value: 'openai' },
    ],
  }]);

  console.log('');
  console.log(chalk.bold('  Next steps:'));
  console.log('');
  console.log(`    1. ${chalk.green(`mutagent integrate ${framework}`)}`);
  console.log(`       Get the integration guide for ${framework}`);
  console.log('');
  console.log(`    2. ${chalk.green(`mutagent integrate ${framework} --verify`)}`);
  console.log('       Verify your integration is working');
  console.log('');
  console.log(`    3. ${chalk.green('mutagent explore')}`);
  console.log('       Re-scan to confirm markers are in place');
  console.log('');
}

function runOptimizationPath(promptCount: number, datasetCount: number): void {
  console.log(chalk.bold('  Quick Optimization Workflow:'));
  console.log('');

  let step = 1;

  if (promptCount === 0) {
    console.log(`    ${String(step)}. ${chalk.green('mutagent prompts create --name "my-prompt" --raw "Your prompt with {{variables}}"')}`);
    console.log('       Create a prompt with template variables');
    console.log('');
    step++;
  } else {
    console.log(chalk.dim(`    ${chalk.green('\u2713')} Prompts detected in codebase (${String(promptCount)} found)`));
    console.log(chalk.dim(`      Upload one: mutagent prompts create --name "my-prompt" --raw-file <path>`));
    console.log('');
  }

  if (datasetCount === 0) {
    console.log(`    ${String(step)}. ${chalk.green('mutagent prompts dataset add <prompt-id> -d \'[{"input":{...},"expectedOutput":{...}}]\' --name "my-dataset"')}`);
    console.log('       Upload a dataset with input/output pairs');
    console.log('');
    step++;
  } else {
    console.log(chalk.dim(`    ${chalk.green('\u2713')} Datasets detected in codebase (${String(datasetCount)} found)`));
    console.log(chalk.dim(`      Upload one: mutagent prompts dataset add <prompt-id> -d '[...]' --name "my-dataset"`));
    console.log('');
  }

  console.log(`    ${String(step)}. ${chalk.green(`mutagent prompts evaluation create <prompt-id> --name "My Eval" -d '{"evalConfig":{"criteria":[...]}}'`)}`);
  console.log('       Define evaluation criteria');
  console.log('');
  step++;

  console.log(`    ${String(step)}. ${chalk.green('mutagent prompts optimize start <prompt-id> --dataset <dataset-id>')}`);
  console.log('       Start the optimization loop');
  console.log('');

  console.log(chalk.dim('  Tip: Use `mutagent prompts list` to see your prompt IDs.'));
  console.log('');
}
